type LiffSdk = {
  isApiAvailable?: (apiName: string) => boolean;
  getDecodedIDToken?: () => {
    aud?: unknown;
    exp?: unknown;
    iat?: unknown;
    iss?: unknown;
    sub?: unknown;
  } | null;
  getIDToken: () => string | null;
  init: (options: { liffId: string }) => Promise<void>;
  isInClient?: () => boolean;
  isLoggedIn: () => boolean;
  login: (options?: { redirectUri?: string }) => void;
  logout?: () => void;
  shareTargetPicker?: (messages: Array<{ type: "text"; text: string }>) => Promise<unknown>;
};

declare global {
  interface Window {
    liff?: LiffSdk;
  }
}

const LIFF_SDK_SRC = "https://static.line-scdn.net/liff/edge/2/sdk.js";
const LIFF_LOGIN_RETURN_TO_STORAGE_KEY = "madamuh:liff:returnTo";

let liffScriptPromise: Promise<void> | null = null;
let liffInitPromise: Promise<void> | null = null;
let initializedLiffId: string | null = null;

export class LiffLoginRedirectError extends Error {
  constructor() {
    super("Redirecting to LINE login");
    this.name = "LiffLoginRedirectError";
  }
}

export class LiffLoginIncompleteError extends Error {
  constructor() {
    super("LINE login did not complete");
    this.name = "LiffLoginIncompleteError";
  }
}

export type LiffRuntimeState = {
  isInClient: boolean | null;
  isLoggedIn: boolean | null;
  hasIdToken: boolean | null;
};

function extractExpectedChannelId(liffId: string): string | null {
  const candidate = liffId.split("-")[0]?.trim() ?? "";
  return /^[0-9]+$/.test(candidate) ? candidate : null;
}

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

function isExpectedDecodedToken(
  decodedIdToken: ReturnType<NonNullable<LiffSdk["getDecodedIDToken"]>>,
  liffId: string
): boolean {
  const expectedChannelId = extractExpectedChannelId(liffId);

  if (!decodedIdToken || !expectedChannelId) {
    return true;
  }

  const audience = readNonEmptyString(decodedIdToken.aud);
  const issuer = readNonEmptyString(decodedIdToken.iss);

  if (audience !== expectedChannelId) {
    return false;
  }

  if (issuer && issuer !== "https://access.line.me") {
    return false;
  }

  return true;
}

const TRANSIENT_LIFF_QUERY_PARAMS = [
  "liff.state",
  "access_token",
  "id_token",
  "code",
  "state",
  "friendship_status_changed",
  "liffClientId"
] as const;

const DIAGNOSTIC_REDACTED_QUERY_PARAMS = ["token", "ownerOnboardingToken", "inviteToken"] as const;

export function isSafeRelativeUrl(value: string): boolean {
  return value.startsWith("/") && !value.startsWith("//");
}

export function normalizeRelativeUrlForLiffAuth(value: string): string {
  if (!isSafeRelativeUrl(value)) {
    return "/";
  }

  const normalizedUrl = new URL(value, window.location.origin);
  const nestedReturnTo = normalizedUrl.searchParams.get("liff.state");

  if (nestedReturnTo && isSafeRelativeUrl(nestedReturnTo)) {
    return normalizeRelativeUrlForLiffAuth(nestedReturnTo);
  }

  stripTransientLiffQueryParams(normalizedUrl);

  const sortedSearchEntries = [...normalizedUrl.searchParams.entries()].sort(([leftKey], [rightKey]) => {
    if (leftKey < rightKey) {
      return -1;
    }

    if (leftKey > rightKey) {
      return 1;
    }

    return 0;
  });

  const normalizedSearch = new URLSearchParams(sortedSearchEntries).toString();
  return `${normalizedUrl.pathname}${normalizedSearch ? `?${normalizedSearch}` : ""}`;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function stripTransientLiffQueryParams(url: URL): void {
  for (const paramName of TRANSIENT_LIFF_QUERY_PARAMS) {
    url.searchParams.delete(paramName);
  }
}

export function buildAbsoluteLiffRedirectUri(returnTo: string): string {
  if (isHttpUrl(returnTo)) {
    const absoluteUrl = new URL(returnTo);
    stripTransientLiffQueryParams(absoluteUrl);
    return absoluteUrl.toString();
  }

  return new URL(normalizeRelativeUrlForLiffAuth(returnTo), window.location.origin).toString();
}

export function redactSensitiveUrlForDiagnostics(value: string): string | null {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  try {
    const url = isHttpUrl(normalizedValue)
      ? new URL(normalizedValue)
      : new URL(normalizedValue, window.location.origin);

    stripTransientLiffQueryParams(url);

    for (const paramName of DIAGNOSTIC_REDACTED_QUERY_PARAMS) {
      if (url.searchParams.has(paramName)) {
        url.searchParams.set(paramName, "[redacted]");
      }
    }

    return isHttpUrl(normalizedValue)
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function isMiniAppPermanentLinkUrl(value: string): boolean {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return false;
  }

  try {
    const url = isHttpUrl(normalizedValue)
      ? new URL(normalizedValue)
      : new URL(normalizedValue, window.location.origin);

    return url.origin === "https://miniapp.line.me";
  } catch {
    return false;
  }
}

export function rememberLiffLoginReturnTo(returnTo: string): void {
  if (!isSafeRelativeUrl(returnTo)) {
    return;
  }

  window.sessionStorage.setItem(
    LIFF_LOGIN_RETURN_TO_STORAGE_KEY,
    normalizeRelativeUrlForLiffAuth(returnTo)
  );
}

export function readLiffLoginReturnTo(): string | null {
  const returnTo = window.sessionStorage.getItem(LIFF_LOGIN_RETURN_TO_STORAGE_KEY);

  if (!returnTo || !isSafeRelativeUrl(returnTo)) {
    return null;
  }

  return returnTo;
}

export function clearLiffLoginReturnTo(): void {
  window.sessionStorage.removeItem(LIFF_LOGIN_RETURN_TO_STORAGE_KEY);
}

export function readLiffStateReturnTo(search = window.location.search): string | null {
  const returnTo = new URLSearchParams(search).get("liff.state");

  if (!returnTo || !isSafeRelativeUrl(returnTo)) {
    return null;
  }

  return returnTo;
}

export function listUrlSearchKeys(search = window.location.search): string[] {
  return Array.from(new URLSearchParams(search).keys());
}

export function hasLiffCredentialParams(search = window.location.search): boolean {
  const searchParams = new URLSearchParams(search);
  return TRANSIENT_LIFF_QUERY_PARAMS.some((paramName) => searchParams.has(paramName));
}

function loadLiffScript(): Promise<void> {
  if (window.liff) {
    return Promise.resolve();
  }

  if (liffScriptPromise) {
    return liffScriptPromise;
  }

  liffScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${LIFF_SDK_SRC}"]`);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("LIFF SDK failed to load")), {
        once: true
      });
      return;
    }

    const script = document.createElement("script");
    script.src = LIFF_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("LIFF SDK failed to load"));
    document.head.appendChild(script);
  });

  return liffScriptPromise;
}

export async function initializeLiff(liffId: string): Promise<LiffSdk> {
  if (!liffId) {
    throw new Error("VITE_LINE_MINIAPP_LIFF_ID is not configured");
  }

  await loadLiffScript();

  if (!window.liff) {
    throw new Error("LIFF SDK is not available");
  }

  if (initializedLiffId !== liffId) {
    initializedLiffId = liffId;
    liffInitPromise = window.liff.init({ liffId });
  }

  await liffInitPromise;
  return window.liff;
}

export function resetLiffInitializationState(): void {
  liffInitPromise = null;
  initializedLiffId = null;
}

export async function loginWithRedirect(liffId: string, redirectUri: string): Promise<never> {
  const liff = await initializeLiff(liffId);
  const absoluteRedirectUri = buildAbsoluteLiffRedirectUri(redirectUri);

  if (typeof liff.isInClient === "function" && liff.isInClient()) {
    window.location.replace(absoluteRedirectUri);
    throw new LiffLoginRedirectError();
  }

  liff.login({ redirectUri: absoluteRedirectUri });
  throw new LiffLoginRedirectError();
}

export async function getIdTokenSafe(
  liffId: string,
  options?: {
    timeoutMs?: number;
    intervalMs?: number;
  }
): Promise<string | null> {
  const liff = await initializeLiff(liffId);
  const timeoutMs = options?.timeoutMs ?? (typeof liff.isInClient === "function" && liff.isInClient() ? 1_500 : 250);
  const intervalMs = options?.intervalMs ?? 150;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    const idToken = liff.getIDToken();
    const decodedIdToken = liff.getDecodedIDToken?.() ?? null;

    if (idToken && isExpectedDecodedToken(decodedIdToken, liffId)) {
      return idToken;
    }

    if (idToken && !isExpectedDecodedToken(decodedIdToken, liffId)) {
      throw new Error("LINE ID token does not match the configured LIFF channel");
    }

    if (Date.now() >= deadline) {
      return null;
    }

    await delay(intervalMs);
  }

  return null;
}

export async function getLiffRuntimeState(
  liffId: string,
  options?: {
    timeoutMs?: number;
    intervalMs?: number;
  }
): Promise<LiffRuntimeState> {
  try {
    const timeoutMs = options?.timeoutMs ?? 0;
    const intervalMs = options?.intervalMs ?? 150;
    const deadline = Date.now() + timeoutMs;

    while (true) {
      const liff = await initializeLiff(liffId);
      const runtimeState: LiffRuntimeState = {
        isInClient: typeof liff.isInClient === "function" ? liff.isInClient() : null,
        isLoggedIn: liff.isLoggedIn(),
        hasIdToken: Boolean(liff.getIDToken())
      };

      if (timeoutMs <= 0 || runtimeState.isLoggedIn || runtimeState.hasIdToken || Date.now() >= deadline) {
        return runtimeState;
      }

      await delay(intervalMs);
    }
  } catch {
    return {
      isInClient: null,
      isLoggedIn: null,
      hasIdToken: null
    };
  }
}

export async function isLiffShareTargetPickerAvailable(liffId: string): Promise<boolean> {
  try {
    const liff = await initializeLiff(liffId);
    return typeof liff.isApiAvailable === "function" ? liff.isApiAvailable("shareTargetPicker") : false;
  } catch {
    return false;
  }
}

export async function shareTextWithLiffTargetPicker(liffId: string, text: string): Promise<void> {
  const liff = await initializeLiff(liffId);

  if (typeof liff.isApiAvailable !== "function" || !liff.isApiAvailable("shareTargetPicker")) {
    throw new Error("LIFF shareTargetPicker is not available");
  }

  if (typeof liff.shareTargetPicker !== "function") {
    throw new Error("LIFF shareTargetPicker is not supported by the current SDK runtime");
  }

  await liff.shareTargetPicker([{ type: "text", text }]);
}
